import React, { useId } from 'react';
import { useFormContext } from 'react-hook-form';

import { Accordion, AccordionContent, AccordionItem, Checkbox, Field, FieldGroup, FieldLabel, Input } from '@column/ui';

import { BusinessEntityFormValues } from '../_util/useEntityForm';
import { FormField, RenderFields } from '~/components';
import { SectionWithFields } from '~/components/RenderFields';

import { PercentageInput, REVEAL_ITEM_CLASS } from './EntityFieldInputs';
import { PersonEntitySelector } from './PersonEntitySelector';

// Roles accepted by POST /entities/{entity_id}/associated-persons.
export const ASSOCIATED_PERSON_ROLES: { label: string; value: string }[] = [
  { label: 'Beneficial Owner', value: 'beneficial_owner' },
  { label: 'Control Person', value: 'control_person' },
  { label: 'Account Opener', value: 'account_opener' },
  { label: 'Grantor', value: 'grantor' },
  { label: 'Trustee', value: 'trustee' },
  { label: 'Beneficiary', value: 'beneficiary' },
  { label: 'Card Holder', value: 'card_holder' },
];

const RolesField: React.FC<{
  value: string[];
  disabled?: boolean;
  onChange: (value: string[]) => void;
}> = ({ value, disabled, onChange }) => {
  const uid = useId();
  const toggle = (role: string, checked: boolean) => {
    onChange(checked ? [...value, role] : value.filter((current) => current !== role));
  };
  return (
    <FieldGroup variant="outline">
      {ASSOCIATED_PERSON_ROLES.map((role) => {
        const roleId = `${uid}-${role.value}`;
        return (
          <Field key={role.value} orientation="horizontal">
            <Checkbox
              id={roleId}
              checked={value.includes(role.value)}
              disabled={disabled}
              onCheckedChange={(checked) => toggle(role.value, checked === true)}
            />
            <FieldLabel htmlFor={roleId}>{role.label}</FieldLabel>
          </Field>
        );
      })}
    </FieldGroup>
  );
};

// One associated person: link an existing person entity, then reveal the ownership, roles, and title
// fields once one is chosen. The link field and the details share a single RenderFields `content`
// cell so the details' spacing collapses with them, leaving no gap when nothing is selected.
export const AssociatedPersonFields: React.FC<{ index: number; disabled?: boolean }> = ({ index, disabled }) => {
  const { watch } = useFormContext<BusinessEntityFormValues>();
  const hasPerson = Boolean(watch(`associatedPersons.${index}.personEntityId`));
  const cachedPersonName = watch(`associatedPersons.${index}.personName`);

  const sections: SectionWithFields<BusinessEntityFormValues, undefined>[] = [
    {
      fields: [
        {
          content: (
            <div>
              <FormField<BusinessEntityFormValues>
                id={`associatedPersons.${index}.personEntityId`}
                label="Link Person"
                description="Select the person entity you want to associate with this business."
                rules={{ required: true }}
              >
                {({ value, onChange, onBlur }, { error }) => (
                  <PersonEntitySelector
                    value={(value as string) ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    hasError={!!error}
                    disabled={disabled}
                    fallbackName={cachedPersonName}
                  />
                )}
              </FormField>

              {/* Opened by whether a person is linked; no user-facing trigger. */}
              <Accordion type="single" value={hasPerson ? 'details' : ''}>
                <AccordionItem value="details" className={REVEAL_ITEM_CLASS}>
                  <AccordionContent className="grid gap-6 px-1 pt-7 pb-1">
                    <FormField<BusinessEntityFormValues>
                      id={`associatedPersons.${index}.ownershipPercentage`}
                      label="Ownership Percentage"
                      description="Enter a number between 0 and 100."
                    >
                      {({ value, onChange }) => (
                        <PercentageInput
                          value={value as number | undefined}
                          onChange={onChange}
                          isDisabled={disabled}
                        />
                      )}
                    </FormField>

                    <FormField<BusinessEntityFormValues>
                      id={`associatedPersons.${index}.roles`}
                      label="Roles"
                      rules={{
                        required: 'Select at least one role.',
                        validate: (value) => (Array.isArray(value) && value.length > 0) || 'Select at least one role.',
                      }}
                    >
                      {({ value, onChange }) => (
                        <RolesField value={(value as string[]) ?? []} onChange={onChange} disabled={disabled} />
                      )}
                    </FormField>

                    <FormField<BusinessEntityFormValues>
                      id={`associatedPersons.${index}.titleInBusiness`}
                      label="Title in Business"
                    >
                      {({ value, onChange, onBlur }) => (
                        <Input
                          className="w-full"
                          value={(value as string) ?? ''}
                          onChange={onChange}
                          onBlur={onBlur}
                          placeholder="e.g. Chief Executive Officer"
                          disabled={disabled}
                        />
                      )}
                    </FormField>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          ),
        },
      ],
    },
  ];

  return <RenderFields sections={sections} />;
};
